/**
 * THE PROCESS, ON ONE LINE.
 *
 * Brian: *"the customer journey needs to have dates. I want to know the entire
 * process for this"* and *"visually this has to be very clear."*
 *
 * So the hierarchy is deliberate. Biggest and first is the answer to the only
 * question a coach ever asks — **when will my rings be here** — and whether we
 * beat it. The milestone dates sit under it as the evidence.
 *
 * Three things are drawn differently on purpose because they mean different
 * things, and flattening them is how a screen starts lying:
 *
 *   RECORDED    a date somebody stamped. Solid.
 *   EXPECTED    computed from the 12–16 week window. Outlined, never solid.
 *   REQUESTED   what the customer ASKED for. Not a delivery, and if it falls
 *               before our earliest it is drawn as a conflict, because that is
 *               a conversation owed today rather than in week ten.
 *
 * Beating the window is the one unambiguously good number in the whole system,
 * so it gets the strongest treatment on the card.
 */
import s from './Timeline.module.css'

const fmt = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null
}
const fmtShort = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null
}

const CLOCK_LABEL = {
  not_approved: 'Clock not started',
  no_date: 'Approved, undated',
  building: 'In build',
  due: 'Due now',
  overdue: 'Past the window',
  delivered: 'Delivered',
}

export default function Timeline({ timeline }) {
  if (!timeline) return null
  const b = timeline.build || {}
  const pts = timeline.points || []
  const recorded = pts.filter((p) => p.recorded)

  return (
    <div className={s.wrap}>
      {/* ---- THE HEADLINE ------------------------------------------------ */}
      <div className={s.clock} data-state={b.state}>
        {b.state === 'delivered' ? (
          b.beatWindow ? (
            <>
              <div className={s.beat}>
                Beat the window by {b.beatByDays} day{b.beatByDays === 1 ? '' : 's'}
              </div>
              <div className={s.clockSub}>
                Built in {b.tookDays} days against a {b.weeks} estimate. Delivered {fmt(b.actualISO)}.
              </div>
            </>
          ) : (
            <>
              <div className={s.clockHead}>
                Delivered {fmt(b.actualISO)}
                {b.lateByDays > 0 && <span className={s.late}> · {b.lateByDays}d past the window</span>}
              </div>
              <div className={s.clockSub}>{b.reason}</div>
            </>
          )
        ) : b.started ? (
          <>
            <div className={s.clockHead}>
              Expected {fmtShort(b.earliestISO)} – {fmt(b.latestISO)}
            </div>
            <div className={s.clockSub}>
              Clock started {fmt(b.clockStartISO)} when everything was approved. {b.reason}
            </div>
          </>
        ) : (
          <>
            <div className={s.clockHead}>{CLOCK_LABEL[b.state] || 'No completion estimate'}</div>
            <div className={s.clockSub}>{b.reason}</div>
          </>
        )}
      </div>

      {/* ---- THE CONFLICT ------------------------------------------------
        * A date the customer asked for that we cannot hit. The single most
        * useful thing on this card, and it exists nowhere else today. */}
      {b.customerWants && (
        <div className={s.wants} data-ok={b.customerWants.achievable ? '1' : '0'}>
          <span className={s.wantsLabel}>Customer asked for</span>
          <strong>{fmt(b.customerWants.atISO)}</strong>
          <span className={s.wantsReason}>{b.customerWants.reason}</span>
        </div>
      )}

      {/* ---- THE DATES --------------------------------------------------- */}
      {recorded.length > 0 ? (
        <ol className={s.steps}>
          {pts.map((p) => (
            <li
              key={p.key}
              className={s.step}
              data-recorded={p.recorded ? '1' : '0'}
              data-projected={p.projected ? '1' : '0'}
              data-clock={p.key === 'allApproved' ? '1' : '0'}
            >
              <span className={s.dot} aria-hidden="true" />
              <span className={s.stepLabel}>{p.label}</span>
              <span className={s.stepDate}>
                {p.recorded ? fmt(p.atISO) : <em className={s.missing}>not recorded</em>}
                {p.overdue && <span className={s.overdueTag}> missed</span>}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <div className={s.noDates}>
          No dates recorded on this order yet. The process is invisible until somebody
          stamps one.
        </div>
      )}

      <div className={s.foot}>
        {timeline.recordedCount} of {timeline.totalMilestones} milestones recorded
        {timeline.daysSinceLast != null && timeline.daysSinceLast >= 0 && (
          <> · nothing recorded for {timeline.daysSinceLast} day{timeline.daysSinceLast === 1 ? '' : 's'}</>
        )}
      </div>
    </div>
  )
}
